import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { listSites, type Site } from "../api/sites.ts";
import {
  deleteTwilioCredentials,
  getSiteTwilioConfig,
  getTwilioStatus,
  upsertSiteTwilioConfig,
  upsertTwilioCredentials,
  type SiteTwilioConfig,
} from "../api/twilioAdmin.ts";

export default function TwilioAdminPage() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["twilio-status"], queryFn: getTwilioStatus });
  const { data: sites } = useQuery({ queryKey: ["sites"], queryFn: listSites });

  const [creds, setCreds] = useState({ account_sid: "", auth_token: "", messaging_service_sid: "" });
  const [credsMsg, setCredsMsg] = useState<string | null>(null);

  const saveCreds = useMutation({
    mutationFn: () => upsertTwilioCredentials({
      account_sid: creds.account_sid.trim(),
      auth_token: creds.auth_token.trim(),
      messaging_service_sid: creds.messaging_service_sid.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["twilio-status"] });
      setCreds({ account_sid: "", auth_token: "", messaging_service_sid: "" });
      setCredsMsg("Saved.");
    },
    onError: (e) => setCredsMsg((e as Error).message),
  });

  const removeCreds = useMutation({
    mutationFn: deleteTwilioCredentials,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["twilio-status"] }),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Twilio</h1>

      <section className="card space-y-3">
        <h2 className="text-lg font-semibold">Account credentials</h2>
        <p className="text-sm text-slate-600">
          Status: {status?.configured ? (
            <span className="text-emerald-700">configured{status.enabled ? " · enabled" : " · disabled"}{status.has_messaging_service ? " · SMS-ready" : ""}</span>
          ) : (
            <span className="text-amber-700">not configured</span>
          )}
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Account SID</span>
            <input className="input" placeholder="AC..." value={creds.account_sid} onChange={(e) => setCreds({ ...creds, account_sid: e.target.value })} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Auth Token</span>
            <input type="password" className="input" placeholder="••••••" value={creds.auth_token} onChange={(e) => setCreds({ ...creds, auth_token: e.target.value })} />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block font-medium">Messaging Service SID (optional, for SMS in P3b)</span>
            <input className="input" placeholder="MG..." value={creds.messaging_service_sid} onChange={(e) => setCreds({ ...creds, messaging_service_sid: e.target.value })} />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" className="btn" onClick={() => saveCreds.mutate()} disabled={!creds.account_sid || !creds.auth_token}>
            Save credentials
          </button>
          {status?.configured && (
            <button type="button" className="btn-danger" onClick={() => removeCreds.mutate()}>
              Remove
            </button>
          )}
          {credsMsg && <span className="text-sm text-slate-600">{credsMsg}</span>}
        </div>
      </section>

      <section className="card space-y-2">
        <h2 className="text-lg font-semibold">Per-site call configuration</h2>
        <p className="text-sm text-slate-600">
          For each site, set the Twilio number you bought, the dispatcher numbers to ring during business hours, the
          recording behavior, and after-hours behavior.
        </p>
        <ul className="divide-y divide-slate-200">
          {sites?.map((s) => (
            <SiteConfigRow key={s.id} site={s} />
          ))}
        </ul>
      </section>

      <section className="card text-sm text-slate-600 space-y-2">
        <h2 className="text-lg font-semibold text-slate-900">Twilio Console webhooks</h2>
        <p>Once credentials are saved and a site has a number, configure the Twilio number in the Twilio Console:</p>
        <ul className="ms-5 list-disc space-y-1">
          <li><b>Voice — A call comes in</b>: <code className="rounded bg-slate-100 px-2 py-0.5">https://twilio.websquadinc.com/webhooks/twilio/voice/inbound</code> (HTTP POST)</li>
          <li><b>Voice — Status callback</b>: <code className="rounded bg-slate-100 px-2 py-0.5">https://twilio.websquadinc.com/webhooks/twilio/voice/status</code> (HTTP POST)</li>
          <li><b>Messaging — A message comes in</b>: leave for Phase 3b</li>
        </ul>
      </section>
    </div>
  );
}

function SiteConfigRow({ site }: { site: Site }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["site-twilio", site.id],
    queryFn: () => getSiteTwilioConfig(site.id),
    enabled: open,
  });

  const [form, setForm] = useState<SiteTwilioConfig>({
    twilio_number_e164: null,
    dispatcher_numbers: [],
    business_hours: null,
    recording_enabled: true,
    recording_notice: null,
    after_hours_message: null,
    ivr_enabled: null,
    ivr_welcome_message: null,
    ivr_menu_message: null,
    ivr_voicemail_prompt: null,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: () => upsertSiteTwilioConfig(site.id, form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site-twilio", site.id] }),
  });

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold">{site.name}</div>
          <div className="break-all text-xs text-slate-500">{site.domain} · {site.twilio_number_e164 ?? "no Twilio number"}</div>
        </div>
        <button type="button" className="btn-secondary shrink-0" onClick={() => setOpen((x) => !x)}>
          {open ? "Hide" : "Configure"}
        </button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Twilio number (E.164)</span>
            <input
              className="input"
              placeholder="+17135551234"
              value={form.twilio_number_e164 ?? ""}
              onChange={(e) => setForm({ ...form, twilio_number_e164: e.target.value || null })}
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block font-medium">Dispatcher numbers (comma-separated, E.164)</span>
            <textarea
              className="input min-h-[5rem] resize-y"
              rows={2}
              placeholder="+17135559876, +17135551111"
              value={(form.dispatcher_numbers ?? []).join(", ")}
              onChange={(e) => setForm({ ...form, dispatcher_numbers: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block font-medium">"This call may be recorded" notice (optional)</span>
            <input
              className="input"
              placeholder="(localized default)"
              value={form.recording_notice ?? ""}
              onChange={(e) => setForm({ ...form, recording_notice: e.target.value || null })}
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block font-medium">After-hours / voicemail prompt (optional)</span>
            <input
              className="input"
              placeholder="(localized default)"
              value={form.after_hours_message ?? ""}
              onChange={(e) => setForm({ ...form, after_hours_message: e.target.value || null })}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Recording enabled</span>
            <select
              className="input"
              value={form.recording_enabled ? "yes" : "no"}
              onChange={(e) => setForm({ ...form, recording_enabled: e.target.value === "yes" })}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>

          <div className="md:col-span-2 border-t border-slate-200 pt-4">
            <h3 className="mb-3 text-sm font-semibold">IVR Menu</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  checked={form.ivr_enabled ?? false}
                  onChange={(e) => setForm({ ...form, ivr_enabled: e.target.checked })}
                />
                Enable IVR keypress menu during business hours
              </label>
              <label className="block text-sm md:col-span-2">
                <span className="mb-1 block font-medium">Welcome message</span>
                <input
                  className="input"
                  placeholder="Thank you for calling. Press 1 for a dispatcher, press 2 to leave a message."
                  value={form.ivr_welcome_message ?? ""}
                  onChange={(e) => setForm({ ...form, ivr_welcome_message: e.target.value || null })}
                />
              </label>
              <label className="block text-sm md:col-span-2">
                <span className="mb-1 block font-medium">Voicemail prompt (when caller presses 2 or doesn't press anything)</span>
                <input
                  className="input"
                  placeholder="(uses after-hours message by default)"
                  value={form.ivr_voicemail_prompt ?? ""}
                  onChange={(e) => setForm({ ...form, ivr_voicemail_prompt: e.target.value || null })}
                />
              </label>
            </div>
          </div>

          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block font-medium">Business hours (UTC, JSON)</span>
            <textarea
              className="input h-32 font-mono text-xs"
              placeholder='{"mon":[["13:00","23:00"]], "tue":[["13:00","23:00"]], ... "sat":[], "sun":[]}'
              value={form.business_hours ? JSON.stringify(form.business_hours, null, 2) : ""}
              onChange={(e) => {
                try {
                  setForm({ ...form, business_hours: e.target.value ? JSON.parse(e.target.value) : null });
                } catch {
                  /* leave invalid JSON un-applied */
                }
              }}
            />
            <p className="mt-1 text-xs text-slate-500">
              Empty array = closed for the day. Leave the whole field blank for 24/7. Times are UTC for now (Phase 2 will add per-site timezone conversion).
            </p>
          </label>
          <div className="md:col-span-2 flex items-center gap-3">
            <button type="button" className="btn" onClick={() => save.mutate()}>Save</button>
            {save.isSuccess && <span className="text-sm text-emerald-700">Saved.</span>}
            {save.error && <span className="text-sm text-red-700">{(save.error as Error).message}</span>}
          </div>
        </div>
      )}
    </li>
  );
}
